import { careLogType, ICareLog, ICareLogService } from './types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orderBy } from 'lodash';
import useStores from '../../hooks/useStores';
import {
  DEFAULT_RETRY_DELAY,
  DEFAULT_RETRY_NUMBERS,
} from '../../config/query-client';
import axios from 'axios';
import { useRef } from 'react';
import { isPoundInchType } from '../../components/MeasurementsAction/MeasurementHelper';
import useCareLogTransformData from './useCareLogTransformData';

interface CareLogConfigInterface {
  enabled?: boolean;
}

export default function useCareLogService(
  patientUuid: string,
  minimal = true,
  config?: CareLogConfigInterface
): ICareLogService {
  const { careLogStore, measurementsStore } = useStores();
  const { transformCareLogResult } = useCareLogTransformData(patientUuid);
  const queryClient = useQueryClient();
  const queryKey = ['camel', 'care_log', patientUuid, { get_minimal: minimal }];
  const enabled = config?.enabled ?? true;
  const isImperial = isPoundInchType(measurementsStore.unitsMapping);

  const result = useQuery<{
    result: ICareLog[];
  }>({
    queryKey,
    enabled: !!patientUuid && enabled,
    retry: DEFAULT_RETRY_NUMBERS,
    retryDelay: DEFAULT_RETRY_DELAY,
    select: (data: { result: ICareLog[] }) => {
      return transformCareLogResult(
        data,
        isImperial,
      );
    },
  });
  const { data, isLoading, isError, error } = result;
  const deletedCareLog = useRef({} as ICareLog);

  const updateQueryData = (uuid: string) => {
    deletedCareLog.current = data?.result?.find(
      (carelog) => carelog.uuid === uuid
    ) as ICareLog;
    queryClient.setQueryData(queryKey, (oldData?: { result: ICareLog[] }) => {
      return oldData
        ? {
            result: oldData?.result?.filter((carelog) => carelog.uuid !== uuid),
          }
        : undefined;
    });
  };

  const dismissAction = useMutation({
    mutationFn: async (uuid: string) => {
      updateQueryData(uuid);
      await axios.delete(`/api/clinician/v1/perception/${uuid}`);
      await result.refetch();
    },
    onError: () => {
      queryClient.setQueryData(queryKey, (oldData?: { result: ICareLog[] }) => {
        return oldData
          ? {
              result: [...oldData.result, deletedCareLog.current],
            }
          : undefined;
      });
    },
  });

  return {
    state: careLogStore,
    careLogs: data?.result ?? ([] as ICareLog[]),
    isLoading: !!patientUuid && enabled && isLoading,
    isError,
    error: error as Error,
    dismissCareLog: dismissAction.mutateAsync,
    firstLogin:
      orderBy(
        data?.result?.filter((log) => log.type === careLogType.login),
        'logDatetime',
        'asc'
      )?.[0]?.logDatetime ?? '',
  };
}
